import { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { graphql, createFragmentContainer } from 'react-relay/legacy';

// components
import { PdpRespSimilarItemCarousel } from './PdpRespSimilarItemCarousel/PdpRespSimilarItemCarousel';
import { PdpRespSimilarItemsContainer } from './PdpRespSimilarItemsContainer/PdpRespSimilarItemsContainer';
import { viewSimilarItems, ItemsSimilarToHeader } from '../pdpMessages';
import { PdpSharedUnpurchasableHeader } from '../PdpSharedUnpurchasableIcons/PdpSharedUnpurchasableHeader';
import { Spinner } from 'dibs-elements/exports/Spinner';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';

// actions
import { hasFiredLandingItemsImpressions } from '../../actions/pdpRespSimilarItemsActions';

// helpers
import classnames from 'classnames';
import scrollIntoView from 'scroll-into-view';
import {
    trackItemClick,
    trackEcommerceLandingItemClick,
    trackLandingViewAllClick,
    trackLandingItemImpressions,
    trackItemsDisplayed,
} from '../tracking/similarItemsTracking';

// styles
import styles from './PdpRespSimilarItemsLanding.scss';

// constants
const PURCHASABLE_ITEM_COUNT = 6;

export class PdpRespSimilarItemsLandingComponent extends Component {
    constructor(props) {
        super(props);

        this.hasFiredPdpTileTestTracking = false;
        this.onClickItem = this.onClickItem.bind(this);
        this.onClickViewMore = this.onClickViewMore.bind(this);
        this.handleSectionVisibilityChange = this.handleSectionVisibilityChange.bind(this);
        this.sectionVisibilityRef = createRef();
    }

    componentDidUpdate() {
        const landingItems = this.getLandingItems();
        if (
            landingItems.length &&
            !this.props.similarItemsState.hasFiredImpressions &&
            this.props?.similarItems?.edges?.[0]?.node?.ecommerceTrackingParams
        ) {
            trackLandingItemImpressions(landingItems, this.isPdpTypeDisplayUnpurchasable());
            this.props.updateHasFiredImpressions();
        }
    }

    handleSectionVisibilityChange({ isVisible }) {
        if (isVisible) {
            trackItemsDisplayed({ moduleType: 'PDP_SIMILAR_ITEMS' });
        }
    }

    scrollToPdpRespSimilarItemsGrid() {
        const gridEl = this.props.scrollToRef?.current;
        if (gridEl) {
            scrollIntoView(
                gridEl,
                {
                    time: 500,
                },
                () => gridEl.focus()
            );
        }
    }

    onClickItem(index, event) {
        trackItemClick({ moduleType: 'PDP_SIMILAR_ITEMS' }, event);
        trackEcommerceLandingItemClick({
            index,
            landingItem: this.getLandingItems()[index],
            isSoldPdp: this.isPdpTypeDisplayUnpurchasable(),
        });
    }

    onClickViewMore(event) {
        this.scrollToPdpRespSimilarItemsGrid();

        // fire tracking
        trackLandingViewAllClick(event);
    }

    isPdpTypeDisplayUnpurchasable() {
        const { item } = this.props;
        const isListingNotListed = item.isListingNotListed && !item?.seller?.isInternal;
        return (
            ((item.isListingUnlisted || item.isSold) && !isListingNotListed) ||
            !item?.seller?.isAccessible
        );
    }

    getLandingItems() {
        const similarItems = this.props?.similarItems?.edges || [];

        // Unpurchasable Pdp
        if (this.isPdpTypeDisplayUnpurchasable()) {
            return similarItems;
        }

        return similarItems.slice(0, PURCHASABLE_ITEM_COUNT);
    }

    render() {
        const itemTitle = <i>{this.props.item.title}</i>;
        let landingItems = this.getLandingItems();
        let additionalLandingItems;

        let header;
        let subtitle;
        let isHorizontal = false;

        if (this.isPdpTypeDisplayUnpurchasable()) {
            // Unpurchasable Pdp
            const { item } = this.props;
            header = <PdpSharedUnpurchasableHeader item={item} />;
            subtitle = viewSimilarItems;
        } else {
            // GROWTH-1429: Test Similar Items on Pdp
            subtitle = <ItemsSimilarToHeader title={itemTitle} />;
            isHorizontal = true;
        }

        if (landingItems.length > PURCHASABLE_ITEM_COUNT) {
            additionalLandingItems = landingItems.slice(PURCHASABLE_ITEM_COUNT);
            landingItems = landingItems.slice(0, PURCHASABLE_ITEM_COUNT);
        }

        const itemProps = {
            onClick: this.onClickItem,
            isCondensed: isHorizontal,
        };

        const landingItemsClassNames = classnames(styles.landingItems, 'rowFlex', {
            [styles.isHorizontal]: isHorizontal,
        });
        const landingItemClassNames = classnames('colLg2', 'colMd4', 'colXs6', styles.landingItem, {
            [styles.smallLandingItem]: this.props.landingItemSize === 'small',
        });
        const spinnerClasses = classnames(styles.spinner, {
            [styles.isUnpurchasable]: this.isPdpTypeDisplayUnpurchasable(),
        });

        return (
            <div ref={this.sectionVisibilityRef}>
                <VisibilityTracker
                    elementRef={this.sectionVisibilityRef}
                    onVisibilityChange={this.handleSectionVisibilityChange}
                />
                <PdpRespSimilarItemsContainer
                    isDark
                    isTopContainer
                    header={header}
                    subtitle={subtitle}
                    onClickViewMore={this.onClickViewMore}
                    dataTn="pdp-similar-landing-grid"
                    isHorizontal={isHorizontal}
                    item={this.props.item}
                >
                    {/* Grid of Items */}
                    {!landingItems.length ? (
                        <Spinner containerClass={spinnerClasses} />
                    ) : (
                        <>
                            <div className={landingItemsClassNames}>
                                {landingItems.map(({ node }, index) => (
                                    <div key={node.serviceId} className={landingItemClassNames}>
                                        <PdpRespSimilarItemCarousel
                                            item={node}
                                            index={index}
                                            {...itemProps}
                                        />
                                    </div>
                                ))}
                            </div>
                            {additionalLandingItems ? (
                                <div className={landingItemsClassNames}>
                                    {additionalLandingItems.map(({ node }, index) => (
                                        <div key={node.serviceId} className={landingItemClassNames}>
                                            <PdpRespSimilarItemCarousel
                                                item={node}
                                                index={index + 6}
                                                {...itemProps}
                                            />
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                        </>
                    )}
                </PdpRespSimilarItemsContainer>
            </div>
        );
    }
}

PdpRespSimilarItemsLandingComponent.propTypes = {
    item: PropTypes.object.isRequired,
    similarItems: PropTypes.object,
    user: PropTypes.object,
    scrollToRef: PropTypes.object.isRequired,
    // redux
    similarItemsState: PropTypes.shape({
        hasFiredImpressions: PropTypes.bool.isRequired,
    }).isRequired,
    updateHasFiredImpressions: PropTypes.func.isRequired,
    landingItemSize: PropTypes.string,
};

// Redux Map Functions
function mapStateToProps({ similarItems }) {
    return {
        similarItemsState: {
            hasFiredImpressions: similarItems.ecommerceImpressions.hasFiredLandingItems,
        },
    };
}
function mapDispatchToProps(dispatch) {
    return {
        updateHasFiredImpressions: () => dispatch(hasFiredLandingItemsImpressions()),
    };
}

export const PdpRespSimilarItemsLanding = createFragmentContainer(
    connect(mapStateToProps, mapDispatchToProps)(PdpRespSimilarItemsLandingComponent),
    {
        item: graphql`
            fragment PdpRespSimilarItemsLanding_item on Item {
                title
                isSold
                isListingNotListed
                isListingUnlisted
                seller {
                    isInternal
                    isAccessible
                }
                ...PdpSharedUnpurchasableHeader_item
                ...PdpRespSimilarItemsContainer_item
            }
        `,
        similarItems: graphql`
            fragment PdpRespSimilarItemsLanding_similarItems on moreLikeThisItemConnection {
                edges {
                    node {
                        serviceId
                        categoryPath
                        categoryCode
                        browseUrl
                        title
                        ecommerceTrackingParams
                        ...PdpRespSimilarItemCarousel_item
                    }
                }
            }
        `,
    }
);
